<template>
    <div class="sale_service">
        <header class="service_header">
            <div class="brand_mark" aria-hidden="true"><van-icon name="service-o" /></div>
            <div class="header_copy">
                <h1>设备售后服务</h1>
                <p>查询设备、提交工单并跟踪处理结果</p>
            </div>
            <button class="orders_link" type="button" @click="openOrders">
                <van-icon name="orders-o" />
                <span>售后记录</span>
            </button>
        </header>

        <main class="service_main">
            <section class="search_card" aria-labelledby="sn-search-title">
                <div class="section_eyebrow">DEVICE LOOKUP</div>
                <h2 id="sn-search-title">查询您的设备</h2>
                <p class="section_hint">SN 通常位于设备铭牌或包装标签上</p>
                <form class="search_form" @submit.prevent="searchDevice">
                    <div class="sn_input_wrap" :class="{ focused: inputFocused }">
                        <van-icon name="qr" class="sn_icon" />
                        <input
                            ref="snInput"
                            v-model.trim="deviceSn"
                            type="text"
                            inputmode="text"
                            autocomplete="off"
                            autocapitalize="characters"
                            maxlength="64"
                            placeholder="请输入设备 SN"
                            aria-label="设备 SN"
                            @focus="inputFocused = true"
                            @blur="inputFocused = false"
                        >
                        <button v-if="deviceSn" class="clear_button" type="button" aria-label="清空设备 SN" @click="clearSearch">
                            <van-icon name="clear" />
                        </button>
                        <button class="scan_button" type="button" aria-label="扫描设备码" @click="startScan">
                            <van-icon name="scan" />
                        </button>
                    </div>
                    <button class="search_button" type="submit" :disabled="loading || !deviceSn">
                        <van-loading v-if="loading" size="18" color="#fff" />
                        <span v-else>立即查询</span>
                    </button>
                </form>
            </section>

            <transition name="result-rise" mode="out-in">
                <section v-if="device.device_sn" key="result" class="device_result" aria-live="polite">
                    <div class="result_heading">
                        <div>
                            <div class="section_eyebrow">DEVICE VERIFIED</div>
                            <h2>已找到设备</h2>
                        </div>
                        <span class="verified_badge"><van-icon name="passed" /> 信息已核验</span>
                    </div>

                    <div class="product_summary">
                        <div class="product_visual">
                            <img v-if="productImage" :src="resourceUrl(productImage)" :alt="product.name || '产品图片'" @error="productImage = ''">
                            <div v-else class="image_placeholder"><van-icon name="photo-o" /><span>暂无产品图片</span></div>
                        </div>
                        <div class="product_copy">
                            <h3>{{ product.name || '设备产品' }}</h3>
                            <p>{{ product.intro || '暂无产品简介' }}</p>
                        </div>
                    </div>

                    <dl class="device_facts">
                        <div><dt>设备 SN</dt><dd class="sn_value">{{ device.device_sn }}</dd></div>
                        <div><dt>设备型号</dt><dd>{{ product.code || '-' }}</dd></div>
                        <div><dt>生产日期</dt><dd>{{ productionDate }}</dd></div>
                        <div><dt>检测报告</dt><dd>{{ reportSummary }}</dd></div>
                    </dl>

                    <div v-if="reportLinks.length" class="report_links">
                        <button v-for="report in reportLinks" :key="report.type" type="button" @click="openReport(report.url)">
                            <van-icon name="description" />
                            <span>{{ report.label }}</span>
                            <van-icon name="arrow" />
                        </button>
                    </div>

                    <div class="privacy_note"><van-icon name="shield-o" /><span>仅展示设备售后所需资料，不展示任何仓库、库存或出入库信息</span></div>
                    <section v-if="logisticsVisible" class="logistics_card" aria-labelledby="logistics-title">
                        <div class="logistics_heading">
                            <div>
                                <div class="section_eyebrow">SHIPMENT TRACKING</div>
                                <h3 id="logistics-title">物流信息</h3>
                            </div>
                            <span v-if="logisticsSigned" class="logistics_signed"><van-icon name="passed" /> 已签收</span>
                        </div>
                        <div v-if="!logisticsSigned" class="logistics_body">
                            <div v-if="logistics.waybill_no" class="logistics_waybill">
                                <span>运单号</span><strong>{{ logistics.waybill_no }}</strong>
                            </div>
                            <p class="logistics_event">{{ logistics.last_event || '物流信息待更新' }}</p>
                            <small v-if="logistics.last_event_time">{{ formatLogisticsTime(logistics.last_event_time) }}</small>
                        </div>
                    </section>
                    <section class="history_summary" aria-labelledby="history-title">
                        <div class="history_heading">
                            <h3 id="history-title">该设备售后记录</h3>
                            <button v-if="orders.length" type="button" @click="openOrders">查看全部 {{ orderTotal }}</button>
                        </div>
                        <div v-if="ordersLoading" class="history_state"><van-loading size="20" /> 正在加载</div>
                        <div v-else-if="!orders.length" class="history_state"><van-icon name="orders-o" /> 暂无售后记录</div>
                        <button v-else class="latest_order" type="button" @click="openOrderDetail(orders[0].id)">
                            <span><strong>{{ orders[0].issue_type }}</strong><small>{{ formatTime(orders[0].create_time) }}</small></span>
                            <span class="status_badge" :class="`status_${orders[0].work_status}`">{{ statusText(orders[0].work_status) }}</span>
                            <van-icon name="arrow" />
                        </button>
                    </section>
                    <button class="after_sale_button" :class="{ active_order: hasActiveOrder }" type="button" @click="openAfterSale">
                        <span>{{ hasActiveOrder ? '查看处理中工单' : '申请售后' }}</span><van-icon name="arrow" />
                    </button>
                </section>

                <section v-else-if="searched && !loading" key="empty" class="state_card error_state" aria-live="polite">
                    <div class="state_icon"><van-icon name="warning-o" /></div>
                    <h2>{{ searchError || '未找到该设备' }}</h2>
                    <p>请核对 SN 是否完整，注意字母与数字不要混淆。</p>
                    <button type="button" @click="focusSearch">重新输入</button>
                </section>

            </transition>
        </main>

        <footer class="service_footer"><span>设备信息仅用于售后服务</span></footer>

        <van-popup v-model="showAfterSale" position="bottom" round :style="{ height: '88%' }" closeable @close="saveDraft">
            <div class="form_sheet">
                <div class="sheet_header">
                    <div class="section_eyebrow">SERVICE REQUEST</div>
                    <h2>提交售后工单</h2>
                    <p>可凭设备 SN 查看进度，微信内授权后会同步通知您</p>
                </div>
                <div class="sheet_device"><span>{{ product.name || '设备' }}</span><strong>{{ device.device_sn }}</strong></div>
                <van-field v-model="draft.issueType" readonly clickable label="问题类型" placeholder="请选择" right-icon="arrow" @click="showIssueTypes = true" />
                <van-field v-model.trim="draft.contactName" label="称呼" maxlength="20" placeholder="选填，如王先生" />
                <van-field v-model.trim="draft.phone" label="联系方式" type="tel" maxlength="11" placeholder="选填，如手机号" />
                <van-field v-model.trim="draft.description" label="问题描述" type="textarea" rows="4" maxlength="500" show-word-limit placeholder="请描述设备出现的问题、发生时间及使用环境" />
                <div class="upload_block">
                    <div class="upload_title">问题图片 <span>最多 6 张，单张不超过 5MB</span></div>
                    <van-uploader v-model="draft.files" :max-count="6" :before-read="beforeImageRead" />
                </div>
                <button class="sheet_submit" type="button" :disabled="submitting" @click="submitOrder">
                    <van-loading v-if="submitting" size="18" color="#fff" />
                    <span v-else>提交工单</span>
                </button>
            </div>
        </van-popup>

        <van-action-sheet v-model="showIssueTypes" :actions="issueTypes" cancel-text="取消" close-on-click-action @select="selectIssueType" />

        <van-popup v-model="scanVisible" position="bottom" round :style="{ height: '78%' }" @closed="stopCameraScan">
            <div class="scan_sheet">
                <header><div><div class="section_eyebrow">SCAN DEVICE</div><h2>扫描设备码</h2></div><button type="button" aria-label="关闭扫码" @click="scanVisible = false"><van-icon name="cross" /></button></header>
                <p>将设备铭牌上的条码或二维码放入取景框</p>
                <div id="sale-service-reader" class="scanner_reader"></div>
                <div class="scanner_hint"><van-icon name="scan" />支持纯 SN、条形码及含 device_sn 的设备链接</div>
            </div>
        </van-popup>

        <van-popup v-model="ordersVisible" position="right" :style="{ width: '100%', height: '100%' }">
            <div class="orders_page">
                <header class="subpage_header">
                    <button type="button" aria-label="关闭" @click="ordersVisible = false"><van-icon name="arrow-left" /></button>
                    <div><p>DEVICE SERVICE HISTORY</p><h2>设备售后记录</h2></div>
                </header>

                <div class="status_filters">
                    <button v-for="filter in statusFilters" :key="filter.value" type="button" :class="{ active: orderFilter === filter.value }" @click="changeFilter(filter.value)">{{ filter.label }}</button>
                </div>

                <div class="orders_content">
                    <div v-if="ordersLoading" class="list_state"><van-loading size="30" vertical>加载工单中</van-loading></div>
                    <div v-else-if="!orders.length" class="list_state">
                        <van-icon name="orders-o" />
                        <h3>该设备暂无售后记录</h3>
                        <p>返回设备页即可申请售后</p>
                    </div>
                    <template v-else>
                        <button v-for="order in orders" :key="order.id" class="order_card" type="button" @click="openOrderDetail(order.id)">
                            <div class="order_card_head">
                                <span class="order_no">{{ order.order_no }}</span>
                                <span class="status_badge" :class="`status_${order.work_status}`">{{ statusText(order.work_status) }}</span>
                            </div>
                            <h3>{{ order.product_name || '设备售后' }}</h3>
                            <p class="order_issue">{{ order.issue_type }} · {{ order.description }}</p>
                            <div class="order_meta"><span>SN {{ order.device_sn }}</span><span>{{ formatTime(order.create_time) }}</span></div>
                        </button>
                    </template>
                </div>
            </div>
        </van-popup>

        <van-popup v-model="detailVisible" position="right" :style="{ width: '100%', height: '100%' }">
            <div class="detail_page">
                <header class="detail_nav">
                    <button type="button" aria-label="返回工单列表" @click="detailVisible = false"><van-icon name="arrow-left" /></button>
                    <span>工单详情</span><i aria-hidden="true"></i>
                </header>
                <div v-if="detailLoading" class="list_state"><van-loading size="30" vertical>加载详情中</van-loading></div>
                <template v-else-if="currentOrder.id">
                    <section class="detail_status">
                        <span class="status_badge" :class="`status_${currentOrder.work_status}`">{{ statusText(currentOrder.work_status) }}</span>
                        <h1>{{ currentOrder.product_name || '设备售后工单' }}</h1>
                        <p>{{ currentOrder.order_no }}</p>
                    </section>

                    <main class="detail_content">
                        <section class="detail_card">
                            <h2>设备与问题</h2>
                            <dl>
                                <div><dt>设备 SN</dt><dd class="mono">{{ currentOrder.device_sn }}</dd></div>
                                <div><dt>设备型号</dt><dd>{{ currentOrder.product_model || '-' }}</dd></div>
                                <div><dt>问题类型</dt><dd>{{ currentOrder.issue_type }}</dd></div>
                            </dl>
                            <p class="description_text">{{ currentOrder.description }}</p>
                            <div v-if="currentOrder.images && currentOrder.images.length" class="image_grid">
                                <button v-for="(image, index) in currentOrder.images" :key="image" type="button" @click="previewImages(currentOrder.images, index)"><img :src="resourceUrl(image)" alt="问题图片"></button>
                            </div>
                        </section>

                        <section class="detail_card timeline_card">
                            <h2>处理进度</h2>
                            <ol>
                                <li class="done"><i></i><div><strong>工单已提交</strong><small>{{ formatTime(currentOrder.create_time) }}</small></div></li>
                                <li :class="{ done: currentOrder.accepted_time }"><i></i><div><strong>{{ currentOrder.accepted_time ? '工单已受理' : '等待受理' }}</strong><small>{{ currentOrder.accepted_time ? formatTime(currentOrder.accepted_time) : '工作人员将尽快处理' }}</small></div></li>
                                <li :class="{ done: currentOrder.finished_time }"><i></i><div><strong>{{ currentOrder.finished_time ? statusText(currentOrder.work_status) : '等待处理结果' }}</strong><small>{{ currentOrder.finished_time ? formatTime(currentOrder.finished_time) : '结果将通过微信通知' }}</small></div></li>
                            </ol>
                        </section>

                        <section v-if="currentOrder.finished_time" class="detail_card result_card">
                            <h2>处理结果</h2>
                            <p>{{ currentOrder.handle_result }}</p>
                            <div v-if="currentOrder.handle_images && currentOrder.handle_images.length" class="image_grid">
                                <button v-for="(image, index) in currentOrder.handle_images" :key="image" type="button" @click="previewImages(currentOrder.handle_images, index)"><img :src="resourceUrl(image)" alt="处理结果图片"></button>
                            </div>
                        </section>
                    </main>
                </template>
            </div>
        </van-popup>
    </div>
</template>

<script>
import { ImagePreview } from 'vant'
import { Html5Qrcode } from 'html5-qrcode'
import importedWx from 'weixin-js-sdk'
import saleServiceApi from '@/api/sale_service_api.js'
import until from '@/api/until.js'
import { formatIotmaxTimestamp, parseIotmaxImageList } from '@/utils/iotmaxStock.js'
import { startWechatOpenIdAuth } from '@/utils/saleServiceAuth.js'

const EMPTY_DRAFT = () => ({ issueType: '', contactName: '', phone: '', description: '', files: [] })

export default {
    name: 'SaleService',
    data() {
        return {
            deviceSn: '',
            inputFocused: false,
            loading: false,
            searched: false,
            previewMode: false,
            searchError: '',
            device: {},
            product: {},
            productImage: '',
            reports: {},
            logistics: {},
            showAfterSale: false,
            showIssueTypes: false,
            submitting: false,
            issueTypes: [
                { name: '设备无法启动' },
                { name: '数据或通讯异常' },
                { name: '外观或结构损坏' },
                { name: '其他问题' }
            ],
            draft: EMPTY_DRAFT(),
            ordersVisible: false,
            ordersLoading: false,
            orders: [],
            orderTotal: 0,
            hasActiveOrder: false,
            orderFilter: '',
            statusFilters: [
                { label: '全部', value: '' },
                { label: '待处理', value: 'active' },
                { label: '已完成', value: 'completed' },
                { label: '已驳回', value: 'rejected' }
            ],
            detailVisible: false,
            detailLoading: false,
            currentOrder: {},
            scanVisible: false,
            scanner: null,
            scanStarting: false,
            scanHandling: false,
            wechatScanReady: false,
            wechatScanConfigPromise: null
        }
    },
    computed: {
        appId() {
            return this.$root.app_id || until.getParameter('app_id') || 'wx0154d46e98975549'
        },
        productionDate() {
            const value = this.device.device_time || this.device.create_time
            return value ? formatIotmaxTimestamp(value, { dateOnly: true }) : '-'
        },
        reportSummary() {
            if (this.reports.factory_report && this.reports.shipment_report) return '出厂及出货报告齐全'
            if (this.reports.factory_report) return '已提供出厂报告'
            if (this.reports.shipment_report) return '已提供出货报告'
            return '暂无公开报告'
        },
        reportLinks() {
            const links = []
            if (this.reports.factory_report) links.push({ type: 'factory', label: '查看出厂报告', url: this.reports.factory_report })
            if (this.reports.shipment_report) links.push({ type: 'shipment', label: '查看出货报告', url: this.reports.shipment_report })
            return links
        },
        logisticsSigned() {
            return Number(this.logistics && this.logistics.is_signed || 0) === 1
        },
        logisticsVisible() {
            const item = this.logistics || {}
            return Boolean(item.waybill_no || item.last_event || item.is_signed)
        }
    },
    mounted() {
        document.title = '设备售后服务'
        this.deviceSn = String(this.$route.query.device_sn || this.$route.query.sn || '').trim()
        if (String(this.$route.query.preview || '') === '1') this.loadPreview()
        else if (this.deviceSn) this.searchDevice()
    },
    beforeDestroy() {
        this.stopCameraScan()
    },
    methods: {
        loadPreview() {
            this.previewMode = true
            const activePreview = String(this.$route.query.active || '1') !== '0'
            this.deviceSn = '[设备 SN]'
            this.device = { device_sn: '[设备 SN]', create_time: 0 }
            this.product = { name: '[接口返回产品名称]', code: '[接口返回型号]', intro: '这里展示产品简介，不包含任何仓库或库存数据。' }
            this.orders = [{
                id: 1,
                order_no: '[售后工单号]',
                device_sn: '[设备 SN]',
                product_name: '[接口返回产品名称]',
                product_model: '[接口返回型号]',
                issue_type: '[问题类型]',
                description: '[用户提交的问题描述]',
                images: [],
                work_status: activePreview ? 'processing' : 'completed',
                handle_result: activePreview ? '' : '[工作人员填写的处理结果]',
                handle_images: [],
                create_time: 0,
                accepted_time: 0,
                finished_time: 0
            }]
            this.orderTotal = 1
            this.hasActiveOrder = activePreview
            this.searched = true
        },
        async searchDevice() {
            const sn = String(this.deviceSn || '').trim()
            if (!sn || this.loading) return
            this.loading = true
            this.searched = false
            this.searchError = ''
            this.device = {}
            this.product = {}
            this.productImage = ''
            this.reports = {}
            this.logistics = {}
            const res = await saleServiceApi.publicDevice(this.appId, sn)
            if (res && res.errcode === 0 && res.other) {
                const info = res.other
                this.device = { device_sn: info.device_sn, device_time: info.device_time, create_time: info.create_time }
                this.product = { name: info.device_name, code: info.device_model, intro: info.product_intro || '', imgs: info.product_imgs || '' }
                this.reports = info.reports || {}
                this.logistics = info.logistics || {}
                this.productImage = parseIotmaxImageList(this.product.imgs)[0] || ''
                this.deviceSn = info.device_sn
                this.restoreDraft()
                this.replaceSnQuery(info.device_sn)
                await this.loadOrders()
            } else {
                this.searchError = res && res.status && res.status !== 404 ? '设备信息加载失败' : ''
            }
            this.loading = false
            this.searched = true
            if (this.device.device_sn) {
                const canContinue = await this.ensureWechatNoticeAuth()
                if (canContinue) await this.resumeRouteIntent()
            }
        },
        replaceSnQuery(sn) {
            if (String(this.$route.query.device_sn || '') === String(sn)) return
            const query = { device_sn: sn }
            ;['view', 'order_id'].forEach(key => {
                if (this.$route.query[key]) query[key] = this.$route.query[key]
            })
            this.$router.replace({ path: '/sale_service', query }).catch(() => {})
        },
        clearSearch() {
            this.deviceSn = ''
            this.device = {}
            this.product = {}
            this.reports = {}
            this.logistics = {}
            this.orders = []
            this.orderTotal = 0
            this.hasActiveOrder = false
            this.searched = false
            this.searchError = ''
        },
        focusSearch() {
            this.clearSearch()
            this.$nextTick(() => this.$refs.snInput && this.$refs.snInput.focus())
        },
        resourceUrl(value) {
            if (!value) return ''
            if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value
            return value.startsWith('/') ? value : `/${value}`
        },
        formatLogisticsTime(value) {
            if (!value) return ''
            if (typeof value === 'number' || /^\d+$/.test(String(value))) return this.formatTime(Number(value))
            return String(value)
        },
        openReport(value) {
            window.open(this.resourceUrl(value), '_blank')
        },
        draftKey() {
            return `sale_service_draft_${String(this.device.device_sn || this.deviceSn || '').trim()}`
        },
        saveDraft() {
            if (!this.deviceSn) return
            const payload = {
                issueType: this.draft.issueType,
                contactName: this.draft.contactName,
                phone: this.draft.phone,
                description: this.draft.description
            }
            localStorage.setItem(this.draftKey(), JSON.stringify(payload))
        },
        restoreDraft() {
            try {
                const saved = JSON.parse(localStorage.getItem(this.draftKey()) || '{}')
                this.draft = Object.assign(EMPTY_DRAFT(), saved)
            } catch (e) {
                this.draft = EMPTY_DRAFT()
            }
        },
        currentReturn(extra) {
            const query = new URLSearchParams()
            if (this.device.device_sn || this.deviceSn) query.set('device_sn', this.device.device_sn || this.deviceSn)
            Object.keys(extra || {}).forEach(key => {
                if (extra[key] !== undefined && extra[key] !== '') query.set(key, extra[key])
            })
            return `/sale_service?${query.toString()}`
        },
        isWechatBrowser() {
            const userAgent = window.navigator.userAgent || ''
            return /MicroMessenger/i.test(userAgent) && !/wxwork/i.test(userAgent)
        },
        async ensureWechatNoticeAuth() {
            if (!this.isWechatBrowser()) return true
            const res = await saleServiceApi.wechatStatus(this.appId)
            if (!res || res.errcode !== 0) return true
            if (res.other && res.other.authorized) return true
            if (new URLSearchParams(window.location.search).get('we') === 'ok') {
                this.$toast('微信通知授权未成功，仍可提交并在本页查看结果')
                return true
            }
            this.saveDraft()
            startWechatOpenIdAuth(this.appId, this.currentReturn())
            return false
        },
        async openAfterSale() {
            if (!this.device.device_sn) return this.$toast('请先查询设备')
            if (this.hasActiveOrder) {
                this.orderFilter = ''
                await this.loadOrders()
                const active = this.orders.find(item => ['pending', 'processing'].includes(item.work_status))
                if (active) return this.openOrderDetail(active.id)
                return this.$toast('处理中工单状态已变化，请刷新后再试')
            }
            if (!await this.ensureWechatNoticeAuth()) return
            this.showAfterSale = true
        },
        selectIssueType(action) {
            this.draft.issueType = action.name
        },
        beforeImageRead(file) {
            const files = Array.isArray(file) ? file : [file]
            for (const item of files) {
                if (!/^image\/(jpeg|png|webp)$/i.test(item.type || '')) {
                    this.$toast('仅支持 JPG、PNG 或 WebP 图片')
                    return false
                }
                if (item.size > 5 * 1024 * 1024) {
                    this.$toast('单张图片不能超过 5MB')
                    return false
                }
            }
            return true
        },
        validateDraft() {
            if (!this.draft.issueType) return '请选择问题类型'
            if (this.draft.phone && !/^1\d{10}$/.test(this.draft.phone)) return '请输入正确的联系方式'
            if (!this.draft.description) return '请填写问题描述'
            return ''
        },
        async uploadFiles(entries) {
            const urls = []
            for (const entry of entries || []) {
                if (entry.url && !entry.file) {
                    urls.push(entry.url)
                    continue
                }
                if (!entry.file) continue
                entry.status = 'uploading'
                entry.message = '上传中'
                const res = await saleServiceApi.uploadImage(this.appId, entry.file, this.device.device_sn)
                if (!res || res.errcode !== 0 || !res.other || !res.other.file_url) {
                    entry.status = 'failed'
                    entry.message = '上传失败'
                    throw new Error((res && res.errmsg) || '图片上传失败')
                }
                entry.status = 'done'
                urls.push(res.other.file_url)
            }
            return urls
        },
        async submitOrder() {
            const validation = this.validateDraft()
            if (validation) return this.$toast(validation)
            this.submitting = true
            this.$toast.loading({ message: '正在提交工单', duration: 0, forbidClick: true })
            try {
                const images = await this.uploadFiles(this.draft.files)
                const res = await saleServiceApi.createOrder(this.appId, {
                    device_sn: this.device.device_sn,
                    issue_type: this.draft.issueType,
                    contact_name: this.draft.contactName,
                    phone: this.draft.phone,
                    description: this.draft.description,
                    images: JSON.stringify(images)
                })
                if (!res || res.errcode !== 0) throw new Error((res && res.errmsg) || '工单提交失败')
                localStorage.removeItem(this.draftKey())
                this.draft = EMPTY_DRAFT()
                this.showAfterSale = false
                this.$toast.clear()
                this.$toast.success('售后工单已提交')
                await this.loadOrders()
                if (res.other && res.other.id) await this.openOrderDetail(res.other.id)
            } catch (error) {
                this.$toast.clear()
                this.$toast(error.message || '工单提交失败')
            } finally {
                this.submitting = false
            }
        },
        async openOrders() {
            if (!this.device.device_sn) {
                this.$toast('请先输入或扫描设备 SN')
                this.focusSearch()
                return false
            }
            this.orderFilter = ''
            this.ordersVisible = true
            await this.loadOrders()
            return true
        },
        async loadOrders() {
            if (this.previewMode) {
                this.ordersLoading = false
                return
            }
            this.ordersLoading = true
            const res = await saleServiceApi.listOrders(this.appId, {
                device_sn: this.device.device_sn,
                work_status: this.orderFilter,
                page: 1,
                each_page: 50
            })
            this.ordersLoading = false
            if (res && res.errcode === 0) {
                this.orders = (res.other && res.other.list) || []
                this.orderTotal = Number((res.other && res.other.total) || 0)
                this.hasActiveOrder = Boolean(res.other && res.other.has_active)
            }
            else {
                this.orders = []
                this.orderTotal = 0
                this.$toast((res && res.errmsg) || '工单加载失败')
            }
        },
        changeFilter(value) {
            if (value === this.orderFilter) return
            this.orderFilter = value
            this.loadOrders()
        },
        async openOrderDetail(id) {
            this.detailVisible = true
            if (this.previewMode) {
                this.currentOrder = this.orders.find(item => item.id === id) || {}
                this.detailLoading = false
                return
            }
            this.detailLoading = true
            const res = await saleServiceApi.orderInfo(this.appId, id, this.device.device_sn)
            this.detailLoading = false
            if (res && res.errcode === 0) this.currentOrder = res.other || {}
            else {
                this.detailVisible = false
                this.$toast((res && res.errmsg) || '工单详情加载失败')
            }
        },
        statusText(status) {
            return { pending: '待受理', processing: '处理中', completed: '已完成', rejected: '已驳回' }[status] || status || '-'
        },
        formatTime(value) {
            return value ? formatIotmaxTimestamp(value) : '-'
        },
        previewImages(images, index) {
            ImagePreview({ images: (images || []).map(this.resourceUrl), startPosition: index, closeable: true })
        },
        async resumeRouteIntent() {
            if (this.$route.query.view !== 'orders') return
            this.ordersVisible = true
            if (this.$route.query.order_id) await this.openOrderDetail(Number(this.$route.query.order_id))
        },
        parseScannedSn(value) {
            let text = String(value || '').trim()
            if (!text) return ''
            const commaIndex = text.indexOf(',')
            if (commaIndex > -1 && /^(QR_CODE|CODE_\d+|EAN_\d+|AZTEC|DATA_MATRIX|PDF_417)$/i.test(text.slice(0, commaIndex))) {
                text = text.slice(commaIndex + 1).trim()
            }
            try {
                const parsed = new URL(text, window.location.origin)
                const sn = parsed.searchParams.get('device_sn') || parsed.searchParams.get('dsn') || parsed.searchParams.get('sn')
                if (sn) return sn.trim().slice(0, 64)
                const hashQuery = parsed.hash.includes('?') ? parsed.hash.slice(parsed.hash.indexOf('?') + 1) : ''
                const hashParams = new URLSearchParams(hashQuery)
                const hashSn = hashParams.get('device_sn') || hashParams.get('dsn') || hashParams.get('sn')
                if (hashSn) return hashSn.trim().slice(0, 64)
            } catch (e) {
                // 非 URL 扫码内容按纯 SN 处理。
            }
            return text.replace(/^SN\s*[:：]\s*/i, '').trim().slice(0, 64)
        },
        async useScanResult(value) {
            if (this.scanHandling) return
            const sn = this.parseScannedSn(value)
            if (!sn) return this.$toast('未识别到设备 SN')
            this.scanHandling = true
            try {
                this.deviceSn = sn
                this.scanVisible = false
                await this.stopCameraScan()
                await this.searchDevice()
            } finally {
                this.scanHandling = false
            }
        },
        async startScan() {
            if (this.scanStarting) return
            this.scanStarting = true
            try {
                if (this.isWechatBrowser()) {
                    await this.scanWithWechat()
                    return
                }
                await this.startCameraScan()
            } catch (error) {
                this.$toast((error && error.message) || '扫码启动失败，请手动输入 SN')
            } finally {
                this.scanStarting = false
            }
        },
        getWechatSDK() {
            const candidates = [importedWx, window.jWeixin, window.wx]
            const sdk = candidates.find(item => {
                return item && typeof item.config === 'function' && typeof item.ready === 'function'
            })
            if (!sdk) throw new Error('微信扫码组件加载失败，请刷新页面后重试')
            return sdk
        },
        withTimeout(promise, timeout, message) {
            return new Promise((resolve, reject) => {
                const timer = window.setTimeout(() => reject(new Error(message)), timeout)
                Promise.resolve(promise).then(
                    result => {
                        window.clearTimeout(timer)
                        resolve(result)
                    },
                    error => {
                        window.clearTimeout(timer)
                        reject(error)
                    }
                )
            })
        },
        parseWxConfig(value) {
            let config = value
            if (typeof config === 'string') {
                try {
                    config = JSON.parse(config)
                } catch (error) {
                    throw new Error('微信扫码配置解析失败')
                }
            }
            if (!config || !(config.app_id || config.appId) || !config.timestamp ||
                !(config.nonce_str || config.nonceStr) || !config.signature) {
                throw new Error('微信扫码配置不完整')
            }
            return config
        },
        getWechatScanErrorMessage(error, fallback) {
            const message = error && (error.errMsg || error.err_msg || error.message)
            if (!message) return fallback
            if (/invalid signature/i.test(message)) return '微信扫码签名已失效，请刷新页面后重试'
            if (/permission denied|no permission/i.test(message)) return '公众号尚未授权扫一扫接口'
            if (/function not exist|not support/i.test(message)) return '当前微信版本不支持扫一扫，请升级微信后重试'
            return message.includes(':') ? `${fallback}：${message.split(':').slice(1).join(':')}` : message
        },
        checkWechatScanApi(wxSDK) {
            return new Promise((resolve, reject) => {
                if (typeof wxSDK.checkJsApi !== 'function') {
                    reject(new Error('当前微信版本不支持扫一扫，请升级微信后重试'))
                    return
                }
                wxSDK.checkJsApi({
                    jsApiList: ['scanQRCode'],
                    success: result => {
                        let support = result && result.checkResult
                        if (typeof support === 'string') {
                            try { support = JSON.parse(support) } catch (error) { support = {} }
                        }
                        if (support && (support.scanQRCode === false || support.scanQRCode === 'false')) {
                            reject(new Error('当前微信版本不支持扫一扫，请升级微信后重试'))
                            return
                        }
                        resolve(true)
                    },
                    fail: error => reject(new Error(this.getWechatScanErrorMessage(error, '微信扫码能力校验失败')))
                })
            })
        },
        async createWechatScanConfigPromise() {
            const pageUrl = encodeURI(window.location.href.split('#')[0])
            const res = await this.withTimeout(
                saleServiceApi.wechatJssdk(this.appId, pageUrl),
                15000,
                '获取微信扫码配置超时'
            )
            if (!res || res.errcode !== 0) throw new Error((res && res.errmsg) || '微信扫码初始化失败')
            const config = this.parseWxConfig(res.other)
            const wxSDK = this.getWechatSDK()
            return new Promise((resolve, reject) => {
                let settled = false
                const finish = error => {
                    if (settled) return
                    settled = true
                    window.clearTimeout(timer)
                    if (error) reject(error)
                    else resolve(true)
                }
                const timer = window.setTimeout(() => {
                    finish(new Error('微信扫码初始化超时，请刷新页面后重试'))
                }, 15000)
                wxSDK.ready(() => {
                    this.checkWechatScanApi(wxSDK).then(() => {
                        this.wechatScanReady = true
                        finish()
                    }).catch(finish)
                })
                wxSDK.error(error => {
                    this.wechatScanReady = false
                    finish(new Error(this.getWechatScanErrorMessage(error, '微信扫码签名校验失败')))
                })
                wxSDK.config({
                    debug: false,
                    appId: config.app_id || config.appId,
                    timestamp: config.timestamp,
                    nonceStr: config.nonce_str || config.nonceStr,
                    signature: config.signature,
                    jsApiList: ['checkJsApi', 'scanQRCode']
                })
            })
        },
        async getWechatScanConfig(forceRefresh) {
            if (!this.isWechatBrowser()) throw new Error('请在微信内打开此页面后扫码')
            if (this.wechatScanReady && !forceRefresh) return true
            if (this.wechatScanConfigPromise && !forceRefresh) return this.wechatScanConfigPromise
            this.wechatScanReady = false
            this.wechatScanConfigPromise = this.createWechatScanConfigPromise()
            try {
                return await this.wechatScanConfigPromise
            } catch (error) {
                this.wechatScanConfigPromise = null
                throw error
            }
        },
        async ensureWechatScanConfig() {
            try {
                return await this.getWechatScanConfig(false)
            } catch (firstError) {
                console.warn('微信扫码初始化失败，正在重新获取签名', firstError)
                return this.getWechatScanConfig(true)
            }
        },
        async scanWithWechat() {
            await this.ensureWechatScanConfig()
            const wxSDK = this.getWechatSDK()
            if (typeof wxSDK.scanQRCode !== 'function') {
                throw new Error('当前微信版本不支持扫一扫，请升级微信后重试')
            }
            const result = await new Promise((resolve, reject) => {
                wxSDK.scanQRCode({
                    needResult: 1,
                    scanType: ['qrCode', 'barCode'],
                    success: data => resolve(data.resultStr || ''),
                    cancel: () => resolve(''),
                    fail: error => reject(new Error(this.getWechatScanErrorMessage(error, '微信扫码失败，请重试')))
                })
            })
            if (result) await this.useScanResult(result)
        },
        async startCameraScan() {
            this.scanVisible = true
            await this.$nextTick()
            this.scanner = new Html5Qrcode('sale-service-reader')
            await this.scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 260, height: 180 }, aspectRatio: 1.3 },
                value => this.useScanResult(value),
                () => {}
            )
        },
        async stopCameraScan() {
            if (!this.scanner) return
            const scanner = this.scanner
            this.scanner = null
            try {
                if (scanner.isScanning) await scanner.stop()
                await scanner.clear()
            } catch (e) {
                // 扫码组件可能已随弹层销毁。
            }
        }
    }
}
</script>

<style scoped>
.sale_service {
    --service-blue: #4e89ff;
    --service-blue-dark: #316fe5;
    --service-blue-soft: #e8efff;
    --service-ink: #1f2937;
    --service-muted: #7c8798;
    --service-border: #e8edf2;
    min-height: 100%; height: 100%; overflow-y: auto; background: #f5f8f7; color: var(--service-ink); -webkit-overflow-scrolling: touch;
}
button { font-family: inherit; }
.service_header { position: relative; display: grid; grid-template-columns: 76px minmax(0,1fr) auto; min-height: 190px; padding: 40px 32px 46px; align-items: center; gap: 20px; color: #fff; overflow: hidden; background: linear-gradient(145deg,#316fe5 0%,#4e89ff 64%,#6b9dff 100%); }
.service_header::after { content: ''; position: absolute; right: -86px; bottom: -132px; width: 280px; height: 280px; border: 44px solid rgba(255,255,255,.09); border-radius: 50%; }
.brand_mark { position: relative; z-index: 1; display: grid; width: 76px; height: 76px; place-items: center; border: 1px solid rgba(255,255,255,.42); border-radius: 22px; background: rgba(255,255,255,.14); backdrop-filter: blur(10px); font-size: 38px; }
.header_copy { position: relative; z-index: 1; min-width: 0; }
.header_copy h1 { font-size: 38px; line-height: 1.2; letter-spacing: 1px; }
.header_copy p { margin-top: 10px; color: rgba(255,255,255,.78); font-size: 23px; }
.orders_link { position: relative; z-index: 1; display: flex; min-width: 104px; min-height: 72px; padding: 10px 8px; align-items: center; justify-content: center; flex-direction: column; gap: 4px; border: 0; border-radius: 18px; color: #fff; background: rgba(255,255,255,.12); font-size: 21px; }
.orders_link .van-icon { font-size: 30px; }
.orders_link:active { transform: scale(.96); background: rgba(255,255,255,.2); }
.service_main { position: relative; z-index: 2; width: min(100%,750px); min-height: calc(100vh - 300px); margin: -22px auto 0; padding: 0 28px 42px; }
.search_card,.device_result,.state_card { border: 1px solid rgba(232,237,242,.9); border-radius: 24px; background: #fff; box-shadow: 0 10px 28px rgba(31,41,55,.08); }
.search_card { padding: 32px; }
.section_eyebrow { color: var(--service-blue); font-size: 18px; font-weight: 700; letter-spacing: 2px; }
.search_card h2,.result_heading h2,.state_card h2,.sheet_header h2 { margin-top: 8px; font-size: 34px; line-height: 1.25; }
.section_hint { margin-top: 8px; color: var(--service-muted); font-size: 23px; }
.search_form { display: grid; grid-template-columns: minmax(0,1fr) 164px; gap: 16px; margin-top: 28px; }
.sn_input_wrap { display: flex; min-width: 0; height: 88px; padding: 0 20px; align-items: center; gap: 16px; border: 2px solid var(--service-border); border-radius: 16px; background: #fbfcfd; transition: border-color 180ms ease,box-shadow 180ms ease,background 180ms ease; }
.sn_input_wrap.focused { border-color: var(--service-blue); background: #fff; box-shadow: 0 0 0 6px rgba(78,137,255,.1); }
.sn_icon { flex: 0 0 auto; color: var(--service-blue); font-size: 32px; }
.sn_input_wrap input { width: 100%; min-width: 0; border: 0; outline: 0; color: var(--service-ink); background: transparent; font-size: 28px; font-weight: 600; text-transform: uppercase; }
.clear_button { display: grid; flex: 0 0 48px; width: 48px; height: 48px; place-items: center; border: 0; color: #b2bac5; background: transparent; font-size: 30px; }
.scan_button { display: grid; flex: 0 0 54px; width: 54px; height: 54px; place-items: center; border: 0; border-radius: 14px; color: var(--service-blue-dark); background: var(--service-blue-soft); font-size: 31px; }
.scan_button:active { transform: scale(.94); background: #dce8ff; }
.search_button,.after_sale_button,.sheet_submit { min-height: 88px; border: 0; border-radius: 999px; color: #fff; background: var(--service-blue); box-shadow: 0 10px 20px rgba(78,137,255,.24); font-size: 28px; font-weight: 700; transition: transform 160ms ease,background 160ms ease,box-shadow 160ms ease; }
.search_button:active:not(:disabled),.after_sale_button:active,.sheet_submit:active:not(:disabled) { transform: translateY(2px) scale(.98); background: var(--service-blue-dark); box-shadow: 0 4px 12px rgba(78,137,255,.2); }
.search_button:disabled,.sheet_submit:disabled { color: #a4abb5; background: #e8ebef; box-shadow: none; }
.device_result,.state_card { margin-top: 24px; padding: 32px; }
.result_heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.verified_badge { display: inline-flex; flex: 0 0 auto; min-height: 48px; padding: 0 16px; align-items: center; gap: 8px; border-radius: 999px; color: #318b55; background: #edf8f0; font-size: 20px; font-weight: 600; }
.product_summary { display: grid; grid-template-columns: 168px minmax(0,1fr); gap: 24px; margin-top: 28px; padding: 20px; border-radius: 20px; background: #f7f9fc; }
.product_visual { width: 168px; aspect-ratio: 1; overflow: hidden; border-radius: 16px; background: #fff; }
.product_visual img { width: 100%; height: 100%; object-fit: contain; }
.image_placeholder { display: flex; width: 100%; height: 100%; align-items: center; justify-content: center; flex-direction: column; gap: 10px; color: #a2acb9; font-size: 20px; }
.image_placeholder .van-icon { font-size: 44px; }
.product_copy { align-self: center; min-width: 0; }
.product_copy h3 { font-size: 30px; line-height: 1.35; }
.product_copy p { display: -webkit-box; margin-top: 12px; overflow: hidden; color: var(--service-muted); font-size: 22px; line-height: 1.6; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.device_facts { margin-top: 12px; }
.device_facts > div { display: grid; grid-template-columns: 150px minmax(0,1fr); min-height: 78px; padding: 18px 4px; align-items: center; gap: 20px; border-bottom: 1px solid var(--service-border); }
.device_facts dt { color: var(--service-muted); font-size: 23px; }
.device_facts dd { min-width: 0; color: var(--service-ink); font-size: 25px; font-weight: 600; text-align: right; overflow-wrap: anywhere; }
.device_facts .sn_value,.mono { color: var(--service-blue-dark); font-family: "SFMono-Regular",Consolas,monospace; }
.report_links { display: grid; gap: 12px; margin-top: 22px; }
.report_links button { display: grid; grid-template-columns: 34px minmax(0,1fr) 30px; min-height: 72px; padding: 0 20px; align-items: center; gap: 10px; border: 1px solid #dce6fb; border-radius: 15px; color: #52647d; background: #f8faff; font-size: 22px; text-align: left; }
.report_links .van-icon:first-child { color: var(--service-blue); font-size: 28px; }
.privacy_note { display: flex; margin-top: 22px; padding: 18px 20px; align-items: flex-start; gap: 12px; border-radius: 14px; color: #60728a; background: var(--service-blue-soft); font-size: 21px; line-height: 1.5; }
.privacy_note .van-icon { flex: 0 0 auto; margin-top: 2px; color: var(--service-blue); font-size: 28px; }
.logistics_card { margin-top: 22px; padding: 22px; border: 1px solid #dce6fb; border-radius: 18px; background: #f8faff; }
.logistics_heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.logistics_heading h3 { margin-top: 5px; font-size: 27px; }
.logistics_signed { display: inline-flex; min-height: 48px; padding: 0 16px; align-items: center; gap: 7px; border-radius: 999px; color: #318b55; background: #eaf7ee; font-size: 21px; font-weight: 700; }
.logistics_body { margin-top: 18px; padding: 18px; border-radius: 14px; background: #fff; }
.logistics_waybill { display: flex; align-items: center; justify-content: space-between; gap: 14px; color: var(--service-muted); font-size: 20px; }
.logistics_waybill strong { color: var(--service-blue-dark); font-family: Consolas,monospace; font-size: 22px; overflow-wrap: anywhere; }
.logistics_event { margin-top: 14px; color: var(--service-ink); font-size: 23px; line-height: 1.55; }
.logistics_body small { display: block; margin-top: 8px; color: var(--service-muted); font-size: 19px; }
.history_summary { margin-top: 22px; padding: 22px; border: 1px solid var(--service-border); border-radius: 18px; background: #fbfcfe; }
.history_heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.history_heading h3 { font-size: 25px; }
.history_heading button { border: 0; color: var(--service-blue-dark); background: transparent; font-size: 20px; font-weight: 600; }
.history_state { display: flex; min-height: 80px; align-items: center; justify-content: center; gap: 12px; color: #919ba9; font-size: 21px; }
.history_state > .van-icon { font-size: 29px; }
.latest_order { display: grid; grid-template-columns: minmax(0,1fr) auto 24px; width: 100%; margin-top: 16px; padding: 18px; align-items: center; gap: 12px; border: 0; border-radius: 14px; color: var(--service-ink); background: #fff; text-align: left; box-shadow: 0 4px 14px rgba(31,41,55,.055); }
.latest_order strong,.latest_order small { display: block; }
.latest_order strong { font-size: 22px; }
.latest_order small { margin-top: 5px; color: var(--service-muted); font-size: 18px; }
.latest_order > .van-icon { color: #9ba6b3; font-size: 23px; }
.after_sale_button { display: flex; width: 100%; margin-top: 28px; padding: 0 30px; align-items: center; justify-content: space-between; }
.after_sale_button.active_order { color: var(--service-blue-dark); background: var(--service-blue-soft); box-shadow: none; }
.state_card { display: flex; min-height: 330px; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.state_icon { display: grid; width: 88px; height: 88px; place-items: center; border-radius: 28px; color: #f28a54; background: #fff4ec; font-size: 46px; }
.state_card p { max-width: 480px; margin-top: 14px; color: var(--service-muted); font-size: 23px; }
.state_card button { min-height: 64px; margin-top: 26px; padding: 0 32px; border: 1px solid var(--service-blue); border-radius: 999px; color: var(--service-blue); background: #fff; font-size: 23px; }
.service_footer { display: flex; padding: 4px 32px 42px; align-items: center; flex-direction: column; gap: 4px; color: #9aa4b1; font-size: 19px; }
.form_sheet { height: 100%; padding: 40px 30px 48px; overflow-y: auto; }
.sheet_header { padding-right: 56px; }
.sheet_header p { margin-top: 8px; color: var(--service-muted); font-size: 22px; }
.sheet_device { display: flex; margin: 28px 0 16px; padding: 20px 22px; align-items: center; justify-content: space-between; gap: 20px; border-radius: 16px; color: #52647d; background: var(--service-blue-soft); font-size: 23px; }
.sheet_device strong { color: var(--service-blue-dark); font-family: Consolas,monospace; overflow-wrap: anywhere; }
.form_sheet ::v-deep .van-cell { min-height: 90px; align-items: center; font-size: 25px; }
.form_sheet ::v-deep .van-field__label { width: 138px; }
.upload_block { padding: 26px 32px; }
.upload_title { margin-bottom: 18px; color: #646566; font-size: 25px; }
.upload_title span { margin-left: 8px; color: #a4abb5; font-size: 20px; }
.sheet_submit { display: grid; width: calc(100% - 32px); margin: 18px 16px 0; place-items: center; }
.scan_sheet { height: 100%; padding: 36px 28px 44px; overflow-y: auto; background: #f7f9fc; }
.scan_sheet header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.scan_sheet h2 { margin-top: 8px; font-size: 34px; }
.scan_sheet header > button { display: grid; width: 58px; height: 58px; place-items: center; border: 0; border-radius: 50%; color: #667085; background: #fff; font-size: 29px; }
.scan_sheet > p { margin: 12px 0 24px; color: var(--service-muted); font-size: 22px; }
.scanner_reader { min-height: 420px; overflow: hidden; border: 1px solid #dfe6ef; border-radius: 22px; background: #111827; }
.scanner_reader ::v-deep video { object-fit: cover; }
.scanner_hint { display: flex; margin-top: 20px; padding: 18px 20px; align-items: center; gap: 12px; border-radius: 14px; color: #60728a; background: var(--service-blue-soft); font-size: 20px; line-height: 1.45; }
.scanner_hint .van-icon { color: var(--service-blue); font-size: 28px; }
.orders_page,.detail_page { min-height: 100%; height: 100%; overflow-y: auto; background: #f5f8f7; }
.subpage_header { display: flex; min-height: 178px; padding: 40px 32px 52px; align-items: center; gap: 22px; color: #fff; background: linear-gradient(145deg,#316fe5,#4e89ff 70%,#6b9dff); }
.subpage_header > button,.detail_nav button { display: grid; flex: 0 0 64px; width: 64px; height: 64px; place-items: center; border: 0; border-radius: 50%; color: inherit; background: rgba(255,255,255,.14); font-size: 30px; }
.subpage_header p { color: rgba(255,255,255,.7); font-size: 17px; font-weight: 700; letter-spacing: 2px; }
.subpage_header h2 { margin-top: 5px; font-size: 36px; }
.status_filters { display: flex; padding: 24px 28px 16px; gap: 12px; overflow-x: auto; }
.status_filters button { flex: 0 0 auto; min-height: 56px; padding: 0 24px; border: 1px solid #e1e6ec; border-radius: 999px; color: #758194; background: #fff; font-size: 21px; }
.status_filters button.active { border-color: #cbdcff; color: var(--service-blue-dark); background: var(--service-blue-soft); font-weight: 700; }
.orders_content { padding: 4px 28px 42px; }
.list_state { display: flex; min-height: 430px; padding: 50px 20px; align-items: center; justify-content: center; flex-direction: column; color: #96a0ad; text-align: center; }
.list_state > .van-icon { font-size: 86px; }
.list_state h3 { margin-top: 22px; color: #526075; font-size: 29px; }
.list_state p { margin-top: 10px; font-size: 22px; }
.order_card { display: block; width: 100%; margin-bottom: 18px; padding: 26px; border: 1px solid #e7ecf2; border-radius: 22px; color: var(--service-ink); background: #fff; box-shadow: 0 7px 20px rgba(31,41,55,.055); text-align: left; }
.order_card:active { transform: scale(.99); background: #fbfcfe; }
.order_card_head,.order_meta { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.order_no { color: #8792a1; font-family: Consolas,monospace; font-size: 19px; }
.status_badge { display: inline-flex; min-height: 45px; padding: 0 16px; align-items: center; border-radius: 999px; font-size: 20px; font-weight: 700; }
.status_pending { color: #b96c2e; background: #fff3e8; }
.status_processing { color: #316fe5; background: #e8efff; }
.status_completed { color: #318b55; background: #eaf7ee; }
.status_rejected { color: #c45151; background: #fff0ef; }
.order_card h3 { margin-top: 18px; font-size: 29px; line-height: 1.35; }
.order_issue { display: -webkit-box; margin-top: 10px; overflow: hidden; color: #687588; font-size: 22px; line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.order_meta { margin-top: 20px; color: #9aa4b1; font-size: 19px; }
.detail_nav { position: sticky; top: 0; z-index: 5; display: grid; grid-template-columns: 64px 1fr 64px; min-height: 104px; padding: 18px 28px; align-items: center; color: #fff; background: #3978e9; }
.detail_nav span { font-size: 29px; font-weight: 700; text-align: center; }
.detail_nav button { background: rgba(255,255,255,.13); }
.detail_status { padding: 42px 34px 76px; color: #fff; background: linear-gradient(155deg,#3978e9,#5a91f6); }
.detail_status .status_badge { background: rgba(255,255,255,.18); color: #fff; }
.detail_status h1 { margin-top: 20px; font-size: 38px; line-height: 1.3; }
.detail_status p { margin-top: 8px; color: rgba(255,255,255,.72); font-family: Consolas,monospace; font-size: 20px; }
.detail_content { margin-top: -38px; padding: 0 28px 48px; }
.detail_card { margin-bottom: 20px; padding: 28px; border: 1px solid #e7ecf2; border-radius: 22px; background: #fff; box-shadow: 0 8px 24px rgba(31,41,55,.065); }
.detail_card h2 { font-size: 28px; }
.detail_card dl { margin-top: 18px; }
.detail_card dl > div { display: grid; grid-template-columns: 128px minmax(0,1fr); min-height: 65px; padding: 12px 0; align-items: center; gap: 16px; border-bottom: 1px solid #edf0f4; }
.detail_card dt { color: #8993a1; font-size: 21px; }
.detail_card dd { font-size: 22px; text-align: right; overflow-wrap: anywhere; }
.description_text,.result_card > p { margin-top: 22px; padding: 20px; border-radius: 15px; color: #566478; background: #f6f8fb; font-size: 22px; line-height: 1.7; white-space: pre-wrap; }
.image_grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin-top: 20px; }
.image_grid button { aspect-ratio: 1; overflow: hidden; border: 0; border-radius: 14px; background: #eef1f5; }
.image_grid img { width: 100%; height: 100%; object-fit: cover; }
.timeline_card ol { margin-top: 24px; list-style: none; }
.timeline_card li { position: relative; display: grid; grid-template-columns: 32px 1fr; min-height: 94px; gap: 18px; }
.timeline_card li:not(:last-child)::after { content: ''; position: absolute; left: 14px; top: 30px; width: 2px; height: 64px; background: #e1e6ed; }
.timeline_card li.done:not(:last-child)::after { background: #a9c4ff; }
.timeline_card i { position: relative; z-index: 1; width: 30px; height: 30px; border: 7px solid #e2e6eb; border-radius: 50%; background: #fff; }
.timeline_card li.done i { border-color: var(--service-blue); }
.timeline_card strong,.timeline_card small { display: block; }
.timeline_card strong { font-size: 23px; }
.timeline_card small { margin-top: 8px; color: #939daa; font-size: 20px; }
.result-rise-enter-active,.result-rise-leave-active { transition: opacity 220ms ease,transform 220ms ease; }
.result-rise-enter,.result-rise-leave-to { opacity: 0; transform: translateY(12px); }
@media (max-width: 420px) {
    .service_header { grid-template-columns: 60px minmax(0,1fr) auto; gap: 12px; padding-left: 22px; padding-right: 22px; }
    .brand_mark { width: 60px; height: 60px; border-radius: 18px; font-size: 30px; }
    .header_copy h1 { font-size: 31px; }
    .header_copy p { font-size: 20px; }
    .orders_link { min-width: 84px; }
    .search_form { grid-template-columns: 1fr; }
    .search_button { min-height: 78px; }
}
@media (prefers-reduced-motion: reduce) {
    .result-rise-enter-active,.result-rise-leave-active,.sn_input_wrap,.search_button,.after_sale_button,.sheet_submit { transition-duration: .01ms !important; }
}
</style>
